import { useState, useEffect, useRef, useCallback } from "react";
import { FaEdit, FaSearch, FaTimes } from "react-icons/fa";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import DataTable from "react-data-table-component";
import "../common/style.css";
import "./Employee.css";
import { useNavigate } from "react-router-dom";
import Loading from "../loading/loading";
import { showEmployee, getEmployeeFormLink } from "../http/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ShowEmployee() {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true"
  );

  // Employee state
  const [employees, setEmployees] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const isFetching = useRef(false);
  const searchTimeoutRef = useRef(null);

  // Confirmation modal state
  const [confirmEmployee, setConfirmEmployee] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    localStorage.setItem("sidebarCollapsed", !isSidebarCollapsed);
  };

  // ✅ IMPROVED: Fetch employees with better loading states
  const fetchEmployees = useCallback(async (page = 1, size = perPage, search = "") => {
    if (isFetching.current) {
      console.log('Already fetching, skipping...');
      return;
    }
    
    isFetching.current = true;
    
    // Show search loading only when searching, regular loading for other operations
    if (search !== debouncedSearchTerm) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }

    try {
      console.log('fetchEmployees called with:', { page, size, search });
      
      const response = await showEmployee(page, size, search);
      const data = response.data;

      console.log("API Response data:", data);

      if (data && data.content) {
        setEmployees(data.content || []);
        setTotalRows(data.totalElements || 0);
        console.log(`Set ${data.content.length} employees, total rows: ${data.totalElements}`);
      } else if (Array.isArray(data)) {
        setEmployees(data);
        setTotalRows(data.length);
        console.log(`Set ${data.length} employees from array`);
      } else {
        console.warn("Unexpected API response format:", data);
        setEmployees([]);
        setTotalRows(0);
      }

      setCurrentPage(page);
      console.log('Current page set to:', page);

    } catch (error) {
      console.error("Error fetching employees:", error);
      console.error("Error details:", {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      
      if (error.response?.status === 500) {
        toast.error("Server error occurred while loading employees.");
      } else {
        toast.error("Failed to load employees.");
      }
    } finally {
      setLoading(false);
      setSearchLoading(false);
      isFetching.current = false;
      console.log('Fetch completed');
    }
  }, [perPage, debouncedSearchTerm]);

  // ✅ Initial data load
  useEffect(() => {
    console.log('Initial useEffect triggered');
    fetchEmployees(1, perPage, "");
  }, [fetchEmployees, perPage]);

  // ✅ IMPROVED: Better debounced search with immediate feedback
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set loading state immediately when user starts typing
    if (searchTerm !== debouncedSearchTerm) {
      setSearchLoading(true);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      
      // Reset to page 1 when searching
      if (searchTerm !== debouncedSearchTerm) {
        fetchEmployees(1, perPage, searchTerm);
      }
    }, 300); // Reduced to 300ms for better responsiveness

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, perPage, fetchEmployees, debouncedSearchTerm]);

  // ✅ Clear search function
  const clearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    fetchEmployees(1, perPage, "");
  };

  const handleEdit = (employee) => {
    navigate("/editEmployee", { state: { employee } });
  };

  const handleAdd = () => {
    navigate("/editEmployee");
  };

  const handlePageChange = (page) => {
    console.log('Page changed to:', page);
    fetchEmployees(page, perPage, debouncedSearchTerm);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    console.log('Rows per page changed:', { newPerPage, page });
    setPerPage(newPerPage);
    fetchEmployees(1, newPerPage, debouncedSearchTerm);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle confirmation link click
  const handleConfirmLink = (employee) => {
    setConfirmEmployee(employee);
  };

  const confirmAndProceed = async () => {
    if (!confirmEmployee) return;
    
    try {
      setLoading(true);
      await getEmployeeFormLink(confirmEmployee.id);
      toast.success("Action confirmed successfully!");
      fetchEmployees(currentPage, perPage, debouncedSearchTerm);
    } catch (err) {
      console.error("Confirm action error:", err);
      toast.error("Failed to process action.");
    } finally {
      setConfirmEmployee(null);
      setLoading(false);
    }
  };

  const closeModal = () => {
    setConfirmEmployee(null);
  };

  // ✅ Fixed columns
  const columns = [
    {
      name: "Employee ID",
      selector: (row) => (row.id ? `APITCO/${row.id}` : "UNKNOWN"),
      sortable: true,
      width: "150px",
    },
    {
      name: "Employee Name",
      selector: (row) => row.name || "N/A",
      sortable: true,
      width: "200px",
    },
    {
      name: "Date of Joining",
      selector: (row) =>
        row.joiningDate ? new Date(row.joiningDate).toLocaleDateString() : "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "Resignation Date",
      selector: (row) =>
        row.resignationDate
          ? new Date(row.resignationDate).toLocaleDateString()
          : "-",
      sortable: true,
      width: "150px",
      conditionalCellStyles: [
        {
          when: (row) => row.resignationDate,
          style: {
            backgroundColor: "#f8d7da",
            color: "#842029",
            fontWeight: "bold",
          },
        },
      ],
    },
    {
      name: "Status",
      cell: (row) => {
        const isResigned =
          row.resignationDate &&
          row.resignationDate !== "null" &&
          row.resignationDate !== "";
        return (
          <span
            className="status-badge"
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color: "#fff",
              backgroundColor: isResigned ? "#dc3545" : "#28a745",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {isResigned ? "Resigned" : "Active"}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="action-buttons">
          <button
            className="btn btn-sm btn-secondary me-2"
            onClick={() => handleConfirmLink(row)}
            title="Confirm Action"
            disabled={loading}
          >
            🔗
          </button>
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => handleEdit(row)}
            title="Edit Employee"
            disabled={loading}
          >
            <FaEdit />
          </button>
        </div>
      ),
      allowOverflow: true,
      width: "150px",
    },
  ];



  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
      },
    },
  };


  return (
    <div className="ShowEmployee">
      {loading && <Loading />}
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className={`sb-nav-fixed ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <TopNav toggleSidebar={toggleSidebar} />
        <div id="layoutSidenav">
          <SideNave
            isSidebarCollapsed={isSidebarCollapsed}
            toggleSidebar={toggleSidebar}
          />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <h4 className="mt-4">Employee Management</h4>
                
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <button
                        className="btn bluebtn me-2"
                        onClick={handleAdd}
                        disabled={loading}
                      >
                        + Add Employee
                      </button>
                      
                      <div className="search-box position-relative">
                        <div className="input-group">
                          <span className="input-group-text">
                            {searchLoading ? (
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <FaSearch />
                            )}
                          </span>
                          <input
                            type="text"
                            placeholder="Search by name, ID..."
                            className="form-control"
                            value={searchTerm}
                            onChange={handleSearch}
                            disabled={loading}
                            style={{ width: "300px" }}
                          />
                          {searchTerm && (
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={clearSearch}
                              disabled={loading}
                            >
                              <FaTimes />
                            </button>
                          )}
                        </div>
                        {searchTerm && (
                          <div className="form-text mt-1">
                            {searchLoading ? (
                              <span className="text-muted">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Searching...
                              </span>
                            ) : (
                              <span className="text-muted">
                                Showing results for: "<strong>{debouncedSearchTerm}</strong>"
                                {employees.length > 0 && ` • Found ${totalRows} employee(s)`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <DataTable
                      columns={columns}
                      data={employees}
                      progressPending={loading || searchLoading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationDefaultPage={currentPage}
                      onChangeRowsPerPage={handlePerRowsChange}
                      onChangePage={handlePageChange}
                      highlightOnHover
                      pointerOnHover
                      customStyles={customStyles}
                      noDataComponent={
                        <div className="text-center py-4">
                          {loading || searchLoading ? (
                            "Loading..."
                          ) : debouncedSearchTerm ? (
                            <div>
                              <p>No employees found for "<strong>{debouncedSearchTerm}</strong>"</p>
                              <button 
                                className="btn btn-outline-primary btn-sm mt-2"
                                onClick={clearSearch}
                              >
                                Clear search
                              </button>
                            </div>
                          ) : (
                            "No employees found"
                          )}
                        </div>
                      }
                      persistTableHead
                    />
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmEmployee && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Action</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to proceed with{" "}
                  <b>{confirmEmployee.name}</b> (ID: APITCO/{confirmEmployee.id})?
                </p>
                <p className="text-muted small">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={confirmAndProceed}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}